import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, ChevronUp, Plus, Trash2, Sparkles, TrendingUp, Clock, Rocket, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FoundingBadge } from "@/components/FoundingBadge";
import { InnovatorBadge } from "@/components/InnovatorBadge";

const STATUS_META = {
  open: { label: "Open", cls: "bg-secondary text-muted-foreground" },
  planned: { label: "Planned", cls: "bg-[#F59E0B]/15 text-[#92400e]" },
  shipped: { label: "Shipped", cls: "bg-emerald-100 text-emerald-800" },
  declined: { label: "Not planned", cls: "bg-destructive/10 text-[#991b1b]" },
};
const ADMIN_STATUSES = ["open", "planned", "shipped", "declined"];

// Feature-request board — rendered as a section inside the Rewards (profile) page.
export function IdeasBoard() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [ideas, setIdeas] = useState([]);
  const [sort, setSort] = useState("top");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [notifs, setNotifs] = useState([]);

  const load = useCallback(() => {
    api.get(`/ideas?sort=${sort}`).then((r) => setIdeas(r.data.ideas || [])).catch(() => {});
  }, [sort]);
  useEffect(() => { load(); }, [load]);

  // Notifications: fetch, show, then mark read so the nav dot clears.
  useEffect(() => {
    api.get("/ideas/notifications").then((r) => {
      setNotifs(r.data.notifications || []);
      if (r.data.unread > 0) {
        api.post("/ideas/notifications/read").catch(() => {});
        window.dispatchEvent(new Event("ideas-read"));
      }
    }).catch(() => {});
  }, []);

  const shipped = ideas.filter((i) => i.status === "shipped");

  const submit = async () => {
    if (title.trim().length < 3) { toast.error("Give your idea a short title (3+ characters)."); return; }
    setBusy(true);
    try {
      await api.post("/ideas", { title: title.trim(), description: desc.trim() });
      toast.success("Idea posted — rally some upvotes!");
      setTitle(""); setDesc(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post idea"); }
    setBusy(false);
  };

  const vote = async (idea) => {
    setIdeas((list) => list.map((x) => x.id === idea.id
      ? { ...x, voted: !x.voted, votes: x.votes + (x.voted ? -1 : 1) } : x));
    try { await api.post(`/ideas/${idea.id}/vote`); }
    catch { toast.error("Could not vote"); load(); }
  };

  const remove = async (idea) => {
    setIdeas((list) => list.filter((x) => x.id !== idea.id));
    try { await api.delete(`/ideas/${idea.id}`); toast.success("Removed"); }
    catch { toast.error("Could not remove"); load(); }
  };

  const changeStatus = async (idea, status) => {
    try {
      const r = await api.patch(`/ideas/${idea.id}/status`, { status });
      if (r.data?.innovator_awarded) toast.success(`🏅 ${idea.author_name} earned the Innovator badge!`, { duration: 6000 });
      else toast.success(`Marked "${idea.title}" as ${STATUS_META[status].label}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };

  return (
    <section id="ideas-board" data-testid="ideas-board" className="scroll-mt-20">
      <h2 className="font-display text-lg font-semibold flex items-center gap-2">
        <Lightbulb size={18} strokeWidth={1.8} className="text-brand" /> Ideas &amp; requests
      </h2>
      <p className="text-sm text-muted-foreground mb-3">
        Request a new capability. If it ships, you earn the permanent{" "}
        <span className="inline-flex align-middle"><InnovatorBadge compact /></span> <b>Innovator</b> badge.
      </p>

      {/* Innovator Spotlight */}
      {shipped.length > 0 && (
        <div className="mb-4 rounded-xl border border-emerald-300/60 bg-gradient-to-br from-emerald-50 to-teal-50 p-4" data-testid="innovator-spotlight">
          <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-emerald-700 mb-2">
            <Rocket size={13} strokeWidth={1.8} /> Recently shipped — thanks to you!
          </div>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
            {shipped.map((s) => (
              <div key={s.id} data-testid={`spotlight-${s.id}`}
                className="shrink-0 min-w-[200px] max-w-[260px] bg-white rounded-lg border border-emerald-200 p-3">
                <div className="font-semibold text-sm leading-snug line-clamp-2">{s.title}</div>
                <div className="flex items-center gap-1.5 mt-2 text-[11px] text-muted-foreground">
                  <InnovatorBadge compact /> {s.author_name}{s.is_mine ? " (you)" : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notifications */}
      {notifs.length > 0 && (
        <div className="mb-4 bg-white border border-border rounded-xl p-4" data-testid="idea-notifications">
          <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-2">
            <Bell size={12} strokeWidth={1.8} /> Your idea updates
          </div>
          <div className="space-y-1.5">
            {notifs.slice(0, 6).map((n) => (
              <div key={n.id} data-testid={`idea-notif-${n.id}`}
                className={`text-sm rounded-md px-3 py-2 ${n.read ? "bg-secondary/50 text-muted-foreground" : "bg-brand/[0.06] text-foreground"}`}>
                {n.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submit */}
      <div className="bg-white border border-border rounded-xl p-5 mb-4" data-testid="idea-form">
        <input
          data-testid="idea-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          placeholder="Your idea in a sentence… e.g. Add a study-buddy matcher by major"
          className="w-full bg-background border border-border rounded-md px-3 py-2.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-brand"
        />
        <textarea
          data-testid="idea-desc-input"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          maxLength={1000}
          placeholder="Optional: what problem does it solve and how should it work?"
          className="mt-2 w-full h-20 bg-background border border-border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand resize-none"
        />
        <button
          data-testid="idea-submit-btn"
          onClick={submit}
          disabled={busy}
          className="mt-3 inline-flex items-center gap-1.5 bg-brand text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Plus size={15} strokeWidth={2} /> Post idea
        </button>
      </div>

      {/* Sort */}
      <div className="flex items-center gap-2 mb-4">
        <button data-testid="idea-sort-top" onClick={() => setSort("top")}
          className={`flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 transition-colors ${sort === "top" ? "bg-brand text-white" : "bg-white border border-border hover:border-brand"}`}>
          <TrendingUp size={13} strokeWidth={1.8} /> Top
        </button>
        <button data-testid="idea-sort-new" onClick={() => setSort("new")}
          className={`flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 transition-colors ${sort === "new" ? "bg-brand text-white" : "bg-white border border-border hover:border-brand"}`}>
          <Clock size={13} strokeWidth={1.8} /> New
        </button>
      </div>

      {/* List */}
      {ideas.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid="ideas-empty">
          <Sparkles size={24} strokeWidth={1.5} className="mx-auto mb-2 text-brand" />
          <p className="text-sm text-muted-foreground">No ideas yet — be the first to suggest one and start earning your Innovator badge.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="ideas-list">
          <AnimatePresence initial={false}>
            {ideas.map((idea) => {
              const meta = STATUS_META[idea.status] || STATUS_META.open;
              return (
                <motion.div key={idea.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="bg-white border border-border rounded-xl p-4 flex gap-3" data-testid={`idea-card-${idea.id}`}>
                  <button
                    data-testid={`idea-vote-${idea.id}`}
                    onClick={() => vote(idea)}
                    className={`shrink-0 flex flex-col items-center justify-center w-12 rounded-lg border transition-colors ${idea.voted ? "bg-brand/10 border-brand text-brand" : "bg-white border-border hover:border-brand text-foreground"}`}
                  >
                    <ChevronUp size={18} strokeWidth={2.2} />
                    <span className="mono text-sm font-bold" data-testid={`idea-votes-${idea.id}`}>{idea.votes}</span>
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-sm leading-snug">{idea.title}</h3>
                      <span data-testid={`idea-status-${idea.id}`}
                        className={`mono text-[10px] font-bold uppercase tracking-widest rounded px-2 py-0.5 shrink-0 ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </div>
                    {idea.description && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{idea.description}</p>}
                    <div className="flex items-center flex-wrap gap-1.5 mt-2 text-[11px] text-muted-foreground">
                      <span>by {idea.author_name}{idea.is_mine ? " (you)" : ""}</span>
                      {idea.author_innovator && <InnovatorBadge compact />}
                      {idea.author_founding && <FoundingBadge compact />}
                    </div>

                    {(isAdmin || idea.is_mine) && (
                      <div className="flex items-center gap-2 mt-3">
                        {isAdmin && (
                          <select
                            data-testid={`idea-status-select-${idea.id}`}
                            value={idea.status}
                            onChange={(e) => changeStatus(idea, e.target.value)}
                            className="bg-white border border-border rounded-md px-2 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-brand cursor-pointer"
                          >
                            {ADMIN_STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                          </select>
                        )}
                        <button data-testid={`idea-delete-${idea.id}`} onClick={() => remove(idea)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-destructive transition-colors">
                          <Trash2 size={13} strokeWidth={1.8} /> Remove
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
}
